import useUsersProviders from '@hooks/query/provider/useUsersProviders';
import { updateUserWizardsStatus } from '@services/external/user.api';
import { Loader, StatusBox } from '@ui';
import React from 'react';
import { useMutation } from 'react-query';
import { IUserProviderItem, paringStatusesList, TPairingStatus } from 'types/provider.types';
import queryClient from '../../../query-client';
import ProviderEmpty from './components/provider-empty';
import ProviderInvited from './components/provider-invited';
import ProviderPending from './components/provider-pending';

type TStatusState = 'approvedList' | 'pendingList' | 'emptyList';
type TComponentState = { type: TStatusState; data: IUserProviderItem[] };

const ProviderStatus = () => {
  const { data: providers, isLoading } = useUsersProviders();

  const { mutate: updateWizard } = useMutation(
    async () => {
      await updateUserWizardsStatus({ providerWizard: true });
    },
    {
      onSuccess: React.useCallback(() => {
        queryClient.fetchQuery('user-wizards');
      }, []),
    },
  );

  const mappedProviders = React.useMemo(() => {
    const result = paringStatusesList.reduce((acc, state) => {
      acc[state] = [];
      return acc;
    }, {} as Record<TPairingStatus, IUserProviderItem[]>);

    if (!providers) {
      return result;
    }

    for (const provider of providers) {
      // Check if provider and status are approved
      if (provider.provider.status === 'approved') {
        result[provider.status].push(provider);
      } else {
        // Or push provider to pending crate
        result['pending'].push(provider);
      }
    }

    return result;
  }, [providers]);

  const currentState = React.useMemo<TComponentState>(() => {
    if (mappedProviders.approved.length > 0) {
      return {
        type: 'approvedList',
        data: mappedProviders.approved,
      };
    }

    if (mappedProviders.pending.length > 0) {
      return {
        type: 'pendingList',
        data: mappedProviders.pending,
      };
    }

    return { type: 'emptyList', data: [] };
  }, [mappedProviders]);

  if (isLoading) {
    return <Loader loading size="large" variant="on-light" />;
  }

  if (currentState.type === 'emptyList') {
    return <ProviderEmpty />;
  }

  if (currentState.type === 'approvedList') {
    return <ProviderInvited items={currentState.data} onContinue={updateWizard} />;
  }

  if (currentState.type === 'pendingList') {
    return <ProviderPending items={currentState.data} onContinue={updateWizard} />;
  }

  return <StatusBox title=":/" type="failed" />;
};

export default ProviderStatus;
